import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { canAddToCart } from '@theme/variant-availability';
import { subscribeToBackInStock, subscribeToList, createEvent } from '@theme/klaviyo-utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAGE_VIEW_EVENT = 'Back in Stock Shopify Page View';

/**
 * Resizes a Shopify CDN image via its `width` query param, and returns a full href with a
 * protocol — Klaviyo delivers this image in an email, where there is no page to resolve it
 * against. Both `variant.featured_image.src` and Liquid's `image_url`-filtered fallback are
 * protocol-relative (`//cdn.shopify.com/...`), so the base exists only to supply the scheme;
 * its own host is discarded whenever the src carries one, which Shopify's always do.
 * @param {string | null | undefined} src
 * @param {number} width
 * @returns {string | undefined}
 */
function resizeImage(src, width) {
  if (!src) return undefined;

  const url = new URL(src, 'https://_');
  url.searchParams.set('width', String(width));
  return url.href;
}

/**
 * Shows a back-in-stock signup form when a variant is sold out, and reports signups/views to
 * Klaviyo. `blocks/product-oos-form.liquid` renders one instance per primary/joined product;
 * the primary reacts to `ThemeEvents.variantUpdate`, a joined instance is driven directly via
 * `setVariant()` from `assets/joined-variant-picker.js` (found by `data-product-id`).
 *
 * @typedef {Object} Refs
 * @property {HTMLScriptElement} variantData
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} emailInput
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} successMessage
 * @property {HTMLInputElement} marketingCheckbox
 *
 * @extends {Component<Refs>}
 */
class ProductOosForm extends Component {
  requiredRefs = [
    'variantData',
    'form',
    'emailInput',
    'submitButton',
    'errorMessage',
    'successMessage',
    'marketingCheckbox',
  ];

  #pageViewTracked = false;

  /** Locks the button while a submission is in flight, kept apart from the no-size lock below. */
  #submitting = false;

  /** The variant this instance is about. `{}` until the shopper picks a size. */
  get #variant() {
    return JSON.parse(this.refs.variantData.textContent || '{}') ?? {};
  }

  /**
   * A back-in-stock subscription is raised against one variant, so there is nothing to sign up for
   * until a size is picked — on a fully sold-out product the form paints with none.
   */
  get #hasVariant() {
    return Boolean(this.#variant.id);
  }

  connectedCallback() {
    super.connectedCallback();

    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.updateVisibility);
    this.section?.addEventListener('change', this.#handleFastChange);

    this.#updateSubmitState();
    this.#trackPageViewIfSoldOut();
  }

  #updateSubmitState() {
    this.refs.submitButton.setAttribute('aria-disabled', String(this.#submitting || !this.#hasVariant));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.updateVisibility);
    this.section?.removeEventListener('change', this.#handleFastChange);
  }

  /**
   * Fast path: reveals/hides on raw `change`, ahead of the fetch-gated `variantUpdate` below.
   * Single-option only — a multi-option picker's other fieldset isn't refreshed yet. Writes
   * just the real `id` (from `data-variant-id`); `setVariant()` fills in the rest once
   * `variantUpdate` lands, well before a shopper could realistically submit.
   * @param {Event} event
   */
  #handleFastChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    // Size guide unit toggles (cm/in) are also radios inside variant-picker but carry no
    // option data — skip them so they don't hide the OOS form.
    if (!target.dataset.optionValueId) return;

    const picker = target.closest('variant-picker');
    if (!(picker instanceof HTMLElement) || picker.dataset.productId !== this.dataset.productId) return;
    if (picker.querySelectorAll('fieldset').length !== 1) return;

    if (target.dataset.optionAvailable === 'false') {
      this.#clearSubmissionState();
      this.refs.variantData.textContent = JSON.stringify({ id: target.dataset.variantId });
      this.hidden = false;
    } else {
      this.hidden = true;
    }

    // After the write above — `#clearSubmissionState` runs before the variant lands.
    this.#updateSubmitState();
  };

  /**
   * A print/color swap morphs this element without dispatching `variantUpdate` — markup
   * patches in for free, only the page-view tracking flag needs resetting.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#pageViewTracked = false;
    this.#trackPageViewIfSoldOut();
  }

  /**
   * @param {Event} event
   */
  updateVisibility = (event) => {
    const variantUpdateEvent = /** @type {import('@theme/events').VariantUpdateEvent} */ (event);

    if (variantUpdateEvent.detail.data.newProduct) {
      // Combined-listing switch: carries the new product's id/url, not its title.
      this.dataset.productId = variantUpdateEvent.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    this.setVariant(variantUpdateEvent.detail.resource);
  };

  /**
   * Public: sets the variant this instance is about. Called from `updateVisibility` (primary)
   * or directly by assets/joined-variant-picker.js (joined). `null`/`undefined` hides the form.
   * @param {Record<string, any> | null | undefined} variant
   */
  setVariant(variant) {
    this.refs.variantData.textContent = JSON.stringify(variant ?? {});
    this.#clearSubmissionState();
    this.hidden = !variant || canAddToCart(variant);
    this.#updateSubmitState();
    this.#trackPageViewIfSoldOut();
  }

  /**
   * Back to an unsubmitted form. Leaves the fields alone on purpose: a shopper moving between
   * sold-out sizes is signing up for the same product, so clearing the email they already typed
   * reads as the form throwing their input away.
   */
  #clearSubmissionState() {
    this.refs.form.hidden = false;
    this.refs.successMessage.hidden = true;
    this.refs.errorMessage.hidden = true;
    this.refs.emailInput.removeAttribute('aria-invalid');
  }

  /**
   * Fires once per reveal of a sold-out variant, not on every interaction with it. Only the
   * primary's instance carries `data-track-page-view`.
   */
  #trackPageViewIfSoldOut() {
    if (!('trackPageView' in this.dataset)) return;

    if (this.hidden) {
      this.#pageViewTracked = false;
      return;
    }

    if (this.#pageViewTracked) return;
    this.#pageViewTracked = true;

    this.#sendPageViewEvent();
  }

  /**
   * Skipped for an anonymous shopper — `createEvent()` needs a real identifier to attach
   * the event to a profile, and there's nothing else to send it with.
   */
  #sendPageViewEvent() {
    const email = this.dataset.customerEmail;
    if (!email) return;

    createEvent({ event: PAGE_VIEW_EVENT, properties: this.#buildProductProperties(), email }).catch((error) =>
      console.error(error),
    );
  }

  /**
   * The product-level properties, carrying no size of any kind. A page view reports the sold-out
   * product, not a variant — it fires whether or not a size is picked, so naming the one that
   * happens to be selected would report a size the shopper never asked to be told about.
   * @returns {Record<string, any>}
   */
  #buildProductProperties() {
    return {
      productId: this.dataset.productId,
      productTitle: this.dataset.productTitle,
      imageUrl: resizeImage(this.dataset.productImageUrl, 400),
    };
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#submitting) return;

    if (!this.#hasVariant) {
      this.#showError(this.dataset.selectSizeMessage);
      return;
    }

    const email = this.refs.emailInput.value.trim();

    if (!EMAIL_PATTERN.test(email)) {
      this.refs.emailInput.setAttribute('aria-invalid', 'true');
      this.#showError(this.dataset.invalidEmailMessage);
      return;
    }

    this.refs.emailInput.removeAttribute('aria-invalid');
    this.#submitting = true;
    this.#updateSubmitState();
    this.refs.errorMessage.hidden = true;

    const marketingOptedIn = this.refs.marketingCheckbox.checked;
    const bisListId = this.dataset.bisListId;
    const marketingListId = this.dataset.marketingListId;

    try {
      await Promise.all(
        [
          subscribeToBackInStock({ email, shopifyVariantId: this.#variant.id }),
          bisListId ? subscribeToList({ email, listId: bisListId }) : null,
          marketingOptedIn && marketingListId ? subscribeToList({ email, listId: marketingListId }) : null,
        ].filter(Boolean),
      );

      this.refs.form.hidden = true;
      this.refs.successMessage.hidden = false;
    } catch {
      this.#showError(this.dataset.genericErrorMessage);
    } finally {
      this.#submitting = false;
      this.#updateSubmitState();
    }
  }

  /**
   * @param {string | undefined} message
   */
  #showError(message) {
    this.refs.errorMessage.textContent = message ?? '';
    this.refs.errorMessage.hidden = false;
  }
}

if (!customElements.get('product-oos-form')) {
  customElements.define('product-oos-form', ProductOosForm);
}
